import invariant from 'invariant';
import React from 'react';
import ReactDOMServer from 'react-dom/server';
import * as jsonpack from 'jsonpack';
import { convertHtmlToDraftContent } from "lib/convert/html";
import { convertToRaw, convertFromRaw, ContentState } from "draft-js";
import {
	convertDraftContentToMarkdown,
	buildDraftRawFromMarkdown,
	buildDraftRawFromAstList,
	parseMarkdownToAstList
} from "lib/convert/markdown";
import { DraftViewer } from 'lib';

// 原始转换函数
const _raw2packed = jsonpack.pack;
const _raw2html = raw => ReactDOMServer.renderToStaticMarkup(<DraftViewer raw={raw} />);
const _raw2content = convertFromRaw;
const _content2raw = convertToRaw;
const _content2markdown = convertDraftContentToMarkdown;
const _html2content = convertHtmlToDraftContent;
const _markdown2ast = parseMarkdownToAstList;
const _markdown2raw = buildDraftRawFromMarkdown;
const _ast2raw = buildDraftRawFromAstList;
const _packed2raw = jsonpack.unpack;

// 校验输入合法性
const validate = (format, input) => {
	invariant(
		typeof input !== 'undefined' && input !== null,
		'The %s input to convert is empty!',
		format
	);
	const check = (...conditions) => invariant(
		conditions.reduce((ok, condition) => ok && condition, true),
		'The %s input to convert is not a valid %s format',
		format, format,
	);
	switch (format) {
		case 'raw':
			check(
				typeof input === 'object',
				Array.isArray(input.blocks),
				input.entityMap,
				typeof input.entityMap === 'object',
			);
			break;
		case 'content':
			check(input instanceof ContentState);
			break;
		case 'html':
		case 'markdown':
		case 'packed':
			check(typeof input === 'string');
			break;
		case 'ast':
			check(
				Array.isArray(input),
			);
			break;
		default:
			const error = new Error('Invalid format: ' + format);
			error.framesToPop = 1;
			throw error;
	}
};

const from = (format) => ({
	use: (...pipes) => (input, ...args) => {
		validate(format, input);
		return pipes.reduce((result, pipe) => pipe(result, ...args), input);
	}
});

export const content2html			= from('content').use(_content2raw, _raw2html);
export const content2raw			= from('content').use(_content2raw);
export const content2ast			= from('content').use(_content2markdown, _markdown2ast);
export const content2markdown = from('content').use(_content2markdown);
export const content2packed		= from('content').use(_content2raw, _raw2packed);

export const html2content			= from('html').use(_html2content);
export const html2raw					= from('html').use(_html2content, _content2raw);
export const html2markdown		= from('html').use(_html2content, _content2markdown);
export const html2ast					= from('html').use(_html2content, content2markdown, _markdown2ast);
export const html2packed			= from('html').use(_html2content, _content2raw, _raw2packed);

export const markdown2content = from('markdown').use(_markdown2raw, _raw2content);
export const markdown2html		= from('markdown').use(_markdown2raw, _raw2html);
export const markdown2raw			= from('markdown').use(_markdown2raw);
export const markdown2ast			= from('markdown').use(_markdown2ast);
export const markdown2packed	= from('markdown').use(_markdown2raw, _raw2packed);

export const ast2content			= from('ast').use(_ast2raw, _raw2content);
export const ast2html					= from('ast').use(_ast2raw, _raw2html);
export const ast2markdown			= from('ast').use(_ast2raw, _raw2content, _content2markdown);
export const ast2raw					= from('ast').use(_ast2raw);
export const ast2packed				= from('ast').use(_ast2raw, _raw2packed);

export const raw2content			= from('raw').use(_raw2content);
export const raw2html					= from('raw').use(_raw2html);
export const raw2markdown			= from('raw').use(_raw2content, _content2markdown);
export const raw2ast					= from('raw').use(_raw2content, _content2markdown, _markdown2ast);
export const raw2packed				= from('raw').use(_raw2packed);

export const packed2content		= from('packed').use(_packed2raw, _raw2content);
export const packed2html			= from('packed').use(_packed2raw, _raw2html);
export const packed2markdown	= from('packed').use(_packed2raw, _raw2content, _content2markdown);
export const packed2ast				= from('packed').use(_packed2raw, _raw2content, _content2markdown, _markdown2ast);
export const packed2raw				= from('packed').use(_packed2raw);

const all = {
	content2html,
	content2raw,
	content2ast,
	content2markdown,
	content2packed,
	html2content,
	html2raw,
	html2markdown,
	html2ast,
	html2packed,
	markdown2content,
	markdown2html,
	markdown2raw,
	markdown2ast,
	markdown2packed,
	ast2content,
	ast2html,
	ast2markdown,
	ast2raw,
	ast2packed,
	raw2content,
	raw2html,
	raw2markdown,
	raw2ast,
	raw2packed,
	packed2content,
	packed2html,
	packed2markdown,
	packed2ast,
	packed2raw,
}

const formats = ['Content', 'Html', 'Markdown', 'Ast', 'Raw', 'Packed'];

export const convertFrom = (format, input) => {
	invariant(
		formats.find(x => x.toLowerCase() === format),
		'Invalid format: %s',
		format,
	);
	const helpers = {};
	for (let to of formats) {
		if (format === to.toLowerCase()) {
			helpers[`to${to}`] = () => input;
		} else {
			const helper = all[`${format}2${to.toLowerCase()}`];
			helpers[`to${to}`] = (...args) => helper(input, ...args);
		}
	}
	return helpers;
}



// WEBPACK FOOTER //
// ./lib/convert/all.js